import {
  Center,
  Divider,
  Flex,
  Group,
  Popover,
  ScrollArea,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import {
  IconCheck,
  IconFlask,
  IconDeviceDesktop,
  IconLayersIntersect,
  IconUser,
  IconWorldWww,
} from '@tabler/icons-react';
import AppIcon from '@/components/AppIcon.tsx';
import {
  HiHandRaised,
  HiMiniCheckCircle,
  HiMiniExclamationCircle,
  HiMiniExclamationTriangle,
  HiOutlineFunnel,
} from 'react-icons/hi2';
import StyledButton from '@/components/StyledButton/StyledButton';
import { ExecutionStatus } from '@/types/statuses.ts';
import {
  DEFAULT_RUN_PROVENANCE_STATES,
  DEFAULT_RUN_STATUSES,
  getDefaultRunModes,
  getDefaultRunOrigins,
  RunFilterOption,
  RunMode,
  RunOrigin,
  RunProvenanceState,
} from '@/types/runs.ts';
import AllRunsIcon from '@/assets/icons/all_runs.svg?react';
import InProgressRunsIcon from '@/assets/icons/in_progress_runs.svg?react';
import { ReactNode, useState } from 'react';

type FilterMenuProps = {
  statuses: ExecutionStatus[];
  toggleStatus: (status: ExecutionStatus) => void;
  onlyStatus: (status: ExecutionStatus) => void;
  statusOptions: RunFilterOption<ExecutionStatus>[];
  runModes: RunMode[];
  toggleRunMode: (mode: RunMode) => void;
  onlyRunMode: (mode: RunMode) => void;
  modeOptions: RunFilterOption<RunMode>[];
  runOrigins: RunOrigin[];
  toggleRunOrigin: (origin: RunOrigin) => void;
  onlyRunOrigin: (origin: RunOrigin) => void;
  originOptions: RunFilterOption<RunOrigin>[];
  provenanceStates: RunProvenanceState[];
  toggleProvenanceState: (state: RunProvenanceState) => void;
  onlyProvenanceState: (state: RunProvenanceState) => void;
  provenanceOptions: RunFilterOption<RunProvenanceState>[];
  resetFilters: () => void;
};

const SOURCE_ICON_PROPS = {
  color: '#9ca3af',
  size: 16,
  stroke: 1.8,
};

const ICON_SLOT_STYLE = {
  width: 18,
  minWidth: 18,
  height: 18,
};

const getStatusIcon = (value: ExecutionStatus) => {
  if (value === ExecutionStatus.Active) {
    return <AppIcon CustomIcon={InProgressRunsIcon} size={16} />;
  }

  if (value === ExecutionStatus.AskUser) {
    return (
      <Center w={15} h={15} style={{ borderRadius: '50%', backgroundColor: '#6F74F1' }}>
        <AppIcon Icon={HiHandRaised} size={10} iconProps={{ color: '#F6F6F6' }} />
      </Center>
    );
  }

  if (value === ExecutionStatus.Completed) {
    return <AppIcon Icon={HiMiniCheckCircle} size={18} iconProps={{ color: '#0fa357' }} />;
  }

  if (value === ExecutionStatus.PartiallyCompleted) {
    return <AppIcon Icon={HiMiniExclamationTriangle} size={16} iconProps={{ color: '#FF761F' }} />;
  }

  if (value === ExecutionStatus.Errors) {
    return <AppIcon Icon={HiMiniExclamationCircle} size={18} iconProps={{ color: '#EE393D' }} />;
  }

  return <AppIcon CustomIcon={AllRunsIcon} size={16} color={'#757575'} />;
};

const getSourceIcon = (value: RunOrigin) => {
  if (value === RunOrigin.Manual) return <IconUser {...SOURCE_ICON_PROPS} />;
  if (value === RunOrigin.Portal) return <IconDeviceDesktop {...SOURCE_ICON_PROPS} />;
  if (value === RunOrigin.ExternalTrigger) return <IconWorldWww {...SOURCE_ICON_PROPS} />;

  return <IconLayersIntersect {...SOURCE_ICON_PROPS} />;
};

const getRunModeIcon = (value: RunMode) => {
  if (value === RunMode.Operational) return <IconUser {...SOURCE_ICON_PROPS} />;
  return <IconFlask {...SOURCE_ICON_PROPS} />;
};

const getProvenanceIcon = (value: RunProvenanceState) => {
  if (value === RunProvenanceState.Published) {
    return <AppIcon Icon={HiMiniCheckCircle} size={18} iconProps={{ color: '#0fa357' }} />;
  }
  if (value === RunProvenanceState.Draft) {
    return <IconLayersIntersect {...SOURCE_ICON_PROPS} />;
  }
  if (value === RunProvenanceState.Historical) {
    return <AppIcon CustomIcon={AllRunsIcon} size={16} color={'#757575'} />;
  }

  return <AppIcon Icon={HiHandRaised} size={14} iconProps={{ color: '#6F74F1' }} />;
};

const sectionLabelStyles = {
  color: '#9ca3af',
  fontSize: '12px',
  fontWeight: 700,
  letterSpacing: '0.08em',
  padding: '5px 10px 2px',
  textTransform: 'uppercase' as const,
};

const panelShadow =
  'lch(0 0 0 / 0.02) 0px 6px 18px, lch(0 0 0 / 0.04) 0px 3px 9px, lch(0 0 0 / 0.04) 0px 1px 1px';

const itemButtonStyle = {
  width: '100%',
  borderRadius: '8px',
  padding: '5px 10px',
};

// Reserves a 24px viewport gutter after accounting for the dropdown's 6px padding on each edge.
const FILTER_PANEL_MAX_HEIGHT = 'calc(100dvh - 36px)';

const onlyButtonStyle = {
  borderRadius: '999px',
  padding: '4px 8px',
  minWidth: '44px',
  textAlign: 'center' as const,
};

const hasSameSelections = <T extends string>(selected: T[], defaults: T[]) =>
  selected.length === defaults.length && defaults.every((value) => selected.includes(value));

const FilterPanelSectionLabel = ({ label }: { label: string }) => (
  <Text style={sectionLabelStyles}>{label}</Text>
);

const FilterItemButton = ({
  label,
  selected,
  icon,
  onClick,
}: {
  label: string;
  selected: boolean;
  icon: ReactNode;
  onClick: () => void;
}) => {
  return (
    <UnstyledButton
      onClick={onClick}
      aria-pressed={selected}
      aria-label={label}
      style={itemButtonStyle}
    >
      <Group wrap="nowrap" gap="xs">
        <Center style={ICON_SLOT_STYLE}>{icon}</Center>
        <Text size="sm" c="#0F172A" style={{ flex: 1, textAlign: 'left' }}>
          {label}
        </Text>
        {selected ? (
          <IconCheck size={18} color="var(--velos-brand-pink)" stroke={2.7} />
        ) : (
          <div style={ICON_SLOT_STYLE} />
        )}
      </Group>
    </UnstyledButton>
  );
};

const FilterToggleRow = ({
  rowKey,
  label,
  selected,
  icon,
  onToggle,
  onOnly,
  showOnly,
  setShowOnlyRow,
}: {
  rowKey: string;
  label: string;
  selected: boolean;
  icon: ReactNode;
  onToggle: () => void;
  onOnly: () => void;
  showOnly: boolean;
  setShowOnlyRow: (rowKey: string | null) => void;
}) => {
  return (
    <Flex
      align="center"
      gap="xs"
      onMouseEnter={() => setShowOnlyRow(rowKey)}
      onMouseLeave={() => setShowOnlyRow(null)}
      onFocusCapture={() => setShowOnlyRow(rowKey)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setShowOnlyRow(null);
        }
      }}
    >
      <div style={{ flex: 1 }}>
        <FilterItemButton label={label} selected={selected} icon={icon} onClick={onToggle} />
      </div>
      <div style={{ width: 48, display: 'flex', justifyContent: 'flex-end' }}>
        {showOnly ? (
          <UnstyledButton
            onClick={(event) => {
              event.stopPropagation();
              onOnly();
            }}
            style={onlyButtonStyle}
            aria-label={`Only ${label}`}
          >
            <Text size="xs" fw={600} c="#6B7280">
              Only
            </Text>
          </UnstyledButton>
        ) : null}
      </div>
    </Flex>
  );
};

const FilterMenu = ({
  statuses,
  toggleStatus,
  onlyStatus,
  statusOptions,
  runModes,
  toggleRunMode,
  onlyRunMode,
  modeOptions,
  runOrigins,
  toggleRunOrigin,
  onlyRunOrigin,
  originOptions,
  provenanceStates,
  toggleProvenanceState,
  onlyProvenanceState,
  provenanceOptions,
  resetFilters,
}: FilterMenuProps) => {
  const [activeOnlyRow, setActiveOnlyRow] = useState<string | null>(null);
  const hasModifiedFilters =
    !hasSameSelections(statuses, DEFAULT_RUN_STATUSES) ||
    !hasSameSelections(runModes, getDefaultRunModes()) ||
    !hasSameSelections(runOrigins, getDefaultRunOrigins()) ||
    !hasSameSelections(provenanceStates, DEFAULT_RUN_PROVENANCE_STATES);

  return (
    <Popover
      position="bottom-start"
      width={320}
      withinPortal
      floatingStrategy="fixed"
      middlewares={{
        flip: { padding: 12 },
        shift: { padding: 12 },
        size: true,
      }}
      onClose={() => setActiveOnlyRow(null)}
    >
      <Popover.Target>
        <span>
          <StyledButton
            aria-label="Run filters"
            size={'sm'}
            variant={'subtleIcon'}
            style={{ padding: '5px', width: '25px', height: '25px' }}
          >
            <AppIcon Icon={HiOutlineFunnel} size={16} iconProps={{ strokeWidth: 1.8 }} />
          </StyledButton>
        </span>
      </Popover.Target>
      <Popover.Dropdown
        p={6}
        style={{
          boxShadow: panelShadow,
          borderRadius: '12px',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <ScrollArea.Autosize
          aria-label="Run filter options"
          mah={FILTER_PANEL_MAX_HEIGHT}
          offsetScrollbars="y"
          overscrollBehavior="contain"
          role="region"
          scrollbars="y"
          style={{ flex: 1, minHeight: 0 }}
          type="auto"
        >
          <Stack gap={3}>
            <FilterPanelSectionLabel label="Status" />
            {statusOptions.map((option) => (
              <FilterToggleRow
                key={`status-${option.value}`}
                rowKey={`status-${option.value}`}
                onToggle={() => toggleStatus(option.value)}
                onOnly={() => onlyStatus(option.value)}
                showOnly={activeOnlyRow === `status-${option.value}`}
                setShowOnlyRow={setActiveOnlyRow}
                selected={statuses.includes(option.value)}
                icon={getStatusIcon(option.value)}
                label={option.label}
              />
            ))}
            <Divider my={3} />
            <FilterPanelSectionLabel label="Run Mode" />
            {modeOptions.map((option) => (
              <FilterToggleRow
                key={`mode-${option.value}`}
                rowKey={`mode-${option.value}`}
                label={option.label}
                selected={runModes.includes(option.value)}
                icon={getRunModeIcon(option.value)}
                onToggle={() => toggleRunMode(option.value)}
                onOnly={() => onlyRunMode(option.value)}
                showOnly={activeOnlyRow === `mode-${option.value}`}
                setShowOnlyRow={setActiveOnlyRow}
              />
            ))}
            <Divider my={3} />
            <FilterPanelSectionLabel label="Run Origin" />
            {originOptions.map((option) => (
              <FilterToggleRow
                key={`source-${option.value}`}
                rowKey={`source-${option.value}`}
                label={option.label}
                selected={runOrigins.includes(option.value)}
                icon={getSourceIcon(option.value)}
                onToggle={() => toggleRunOrigin(option.value)}
                onOnly={() => onlyRunOrigin(option.value)}
                showOnly={activeOnlyRow === `source-${option.value}`}
                setShowOnlyRow={setActiveOnlyRow}
              />
            ))}
            <Divider my={3} />
            <FilterPanelSectionLabel label="Version State" />
            {provenanceOptions.map((option) => (
              <FilterToggleRow
                key={`provenance-${option.value}`}
                rowKey={`provenance-${option.value}`}
                label={option.label}
                selected={provenanceStates.includes(option.value)}
                icon={getProvenanceIcon(option.value)}
                onToggle={() => toggleProvenanceState(option.value)}
                onOnly={() => onlyProvenanceState(option.value)}
                showOnly={activeOnlyRow === `provenance-${option.value}`}
                setShowOnlyRow={setActiveOnlyRow}
              />
            ))}
            {hasModifiedFilters ? (
              <>
                <Divider my={3} />
                <StyledButton
                  size="sm"
                  variant="subtle"
                  onClick={resetFilters}
                  style={{ width: '100%', justifyContent: 'flex-start', padding: '4.5px 10px' }}
                >
                  Reset to defaults
                </StyledButton>
              </>
            ) : null}
          </Stack>
        </ScrollArea.Autosize>
      </Popover.Dropdown>
    </Popover>
  );
};

export default FilterMenu;
